import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest, map, debounceTime, distinctUntilChanged, switchMap, catchError, shareReplay, of, EMPTY, retry, timer } from 'rxjs';
import { baseUrl } from 'app/app.config';
import { 
  MaterialRequest, 
  MaterialRequestResponse, 
  MaterialRequestTableData, 
  STATUS_CONFIG,
  resolveStatusInfo 
} from '../models/material-request.model';
import { 
  FilterResponse, 
  MaterialRequestFilters, 
  FilterPreset,
  FilterPresetType,
  FILTER_PRESETS
} from '../models/material-request-filter.model';

@Injectable({
  providedIn: 'root'
})
export class MaterialRequestService {
  private readonly apiUrl = `${baseUrl}/apis/bidding`;
  
  // State management
  private filtersSubject = new BehaviorSubject<MaterialRequestFilters>({});
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private refreshSubject = new BehaviorSubject<void>(undefined);
  private totalElementsSubject = new BehaviorSubject<number>(0);

  // Public observables
  public filters$ = this.filtersSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();
  public totalElements$ = this.totalElementsSubject.asObservable();

  // Cache for filter options
  private filterOptionsCache$: Observable<FilterResponse> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Get paginated material requests with filters
   */
  getMaterialRequests(): Observable<MaterialRequestResponse> {
    return combineLatest([
      this.filters$.pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      ),
      this.refreshSubject
    ]).pipe(
      switchMap(([filters]) => {
        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        const params = this.buildQueryParams(filters);
        
        return this.http.get<MaterialRequestResponse>(
          `${this.apiUrl}/my-requests/paginated`,
          { params }
        ).pipe(
          retry({
            count: 2,
            delay: 1000
          }),
          catchError(error => {
            this.errorSubject.next(this.getErrorMessage(error));
            this.loadingSubject.next(false);
            // Return empty response instead of throwing error to keep the stream alive
            return of({
              content: [],
              totalElements: 0,
              totalPages: 0,
              size: 0,
              number: 0,
              first: true,
              last: true,
              numberOfElements: 0,
              empty: true
            } as MaterialRequestResponse);
          })
        );
      }),
      map(response => {
        this.loadingSubject.next(false);
        this.totalElementsSubject.next(response.totalElements ?? 0);
        return response;
      }),
      shareReplay(1)
    );
  }

  /**
   * Get material requests formatted for table display
   */
  getMaterialRequestsForTable(): Observable<MaterialRequestTableData[]> {
    return this.getMaterialRequests().pipe(
      map(response => response.content.map(mr => this.transformToTableData(mr)))
    );
  }

  /**
   * Exact number of requests the server holds for these filters, under the
   * same visibility rules as the list. Asks for a single row and reads the
   * total, so it is cheap. Used by the stat tiles so a tile's number always
   * equals what its own filter returns. Emits null if the request fails.
   */
  countMaterialRequests(filters: Partial<MaterialRequestFilters>): Observable<number | null> {
    const params = this.buildQueryParams({ ...filters, page: 0, pageSize: 1 });
    return this.http.get<MaterialRequestResponse>(
      `${this.apiUrl}/my-requests/paginated`,
      { params }
    ).pipe(
      map(response => response.totalElements ?? 0),
      catchError(() => of(null))
    );
  }

  /**
   * Get filter options from API
   */
  getFilterOptions(): Observable<FilterResponse> {
    if (!this.filterOptionsCache$) {
      this.filterOptionsCache$ = this.http.get<FilterResponse>(
        `${this.apiUrl}/my-requests/filters-v2`
      ).pipe(
        retry({
          count: 2,
          delay: 1000
        }),
        catchError(error => {
          console.error('Failed to load filter options:', error);
          this.errorSubject.next('Failed to load filter options');
          // Return empty filter response to keep the application working
          return of({
            summary: [],
            filters: []
          } as FilterResponse);
        }),
        shareReplay(1)
      );
    }
    return this.filterOptionsCache$;
  }

  /**
   * Update filters
   */
  updateFilters(filters: Partial<MaterialRequestFilters>): void {
    const currentFilters = this.filtersSubject.value;
    this.filtersSubject.next({ ...currentFilters, ...filters });
  }

  /**
   * Clear all filters but keep pagination params
   */
  clearFilters(): void {
    const currentFilters = this.filtersSubject.value;
    // Preserve pagination params when clearing filters
    this.filtersSubject.next({
      page: currentFilters.page ?? 0,
      pageSize: currentFilters.pageSize ?? 10
    });
  }

  /**
   * Reset filters to default
   */
  resetFilters(): void {
    this.filtersSubject.next({
      page: 0,
      pageSize: 10,
    });
  }

  /**
   * Apply predefined filter preset
   */
  applyFilterPreset(presetType: FilterPresetType): void {
    const preset = FILTER_PRESETS[presetType];
    this.updateFilters(preset);
  }

  /**
   * Get preset configuration
   */
  getFilterPresetConfig(presetType: FilterPresetType): Partial<MaterialRequestFilters> {
    return FILTER_PRESETS[presetType];
  }

  /**
   * Get current filters
   */
  getCurrentFilters(): MaterialRequestFilters {
    return this.filtersSubject.value;
  }

  /**
   * Refresh data
   */
  refresh(): void {
    this.errorSubject.next(null); // Clear any previous errors
    this.refreshSubject.next();
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSubject.next(null);
  }

  /**
   * Reset filter options cache to force reload
   */
  resetFilterOptionsCache(): void {
    this.filterOptionsCache$ = null;
  }

  /**
   * Get material request by ID
   */
  getMaterialRequestById(id: number): Observable<MaterialRequest> {
    return this.http.get<MaterialRequest>(`${this.apiUrl}/my-requests/${id}`);
  }

  /**
   * Export material requests
   */
  exportMaterialRequests(format: 'excel' | 'csv' | 'pdf' = 'excel'): Observable<Blob> {
    const filters = this.filtersSubject.value;
    const params = this.buildQueryParams(filters);
    params.set('format', format);

    return this.http.get(`${this.apiUrl}/my-requests/export`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Save filter preset
   */
  saveFilterPreset(name: string, description?: string): Observable<FilterPreset> {
    const filters = this.filtersSubject.value;
    const preset: Omit<FilterPreset, 'id'> = {
      name,
      description,
      filters,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return this.http.post<FilterPreset>(`${this.apiUrl}/filter-presets`, preset);
  }

  /**
   * Get saved filter presets
   */
  getFilterPresets(): Observable<FilterPreset[]> {
    return this.http.get<FilterPreset[]>(`${this.apiUrl}/filter-presets`).pipe(
      catchError(error => {
        console.error('Failed to load filter presets:', error);
        return of([]);
      })
    );
  }

  /**
   * Delete filter preset
   */
  deleteFilterPreset(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/filter-presets/${id}`);
  }

  /**
   * Search material requests with advanced search
   */
  searchMaterialRequests(query: string, fields: string[] = []): Observable<MaterialRequestTableData[]> {
    const params = new HttpParams()
      .set('q', query)
      .set('fields', fields.join(','));

    return this.http.get<MaterialRequestResponse>(
      `${this.apiUrl}/my-requests/search`,
      { params }
    ).pipe(
      map(response => response.content.map(mr => this.transformToTableData(mr))),
      catchError(error => {
        console.error('Search failed:', error);
        this.errorSubject.next('Search failed. Please try again.');
        return of([]);
      })
    );
  }

  /**
   * Get statistics for dashboard
   */
  getStatistics(): Observable<any> {
    return this.http.get(`${this.apiUrl}/my-requests/statistics`).pipe(
      catchError(error => {
        console.error('Failed to load statistics:', error);
        return of({});
      })
    );
  }

  /**
   * Mark material request as read
   * @param prchOrdrId - The purchase order ID (prch_ordr_id)
   */
  markAsRead(prchOrdrId: number): Observable<{ status: number; status_description: string }> {
    return this.http.post<{ status: number; status_description: string }>(
      `${this.apiUrl}/read-mr/${prchOrdrId}`,
      {}
    );
  }

  /**
   * Mark multiple material requests as read
   * Calls the single mark-as-read endpoint for each ID
   */
  markMultipleAsRead(prchOrdrIds: number[]): Observable<{ status: number; status_description: string }[]> {
    // Call the single endpoint for each ID
    const requests = prchOrdrIds.map(id => this.markAsRead(id));
    return combineLatest(requests);
  }

  /**
   * Update material request status
   */
  updateStatus(id: number, status: string, remarks?: string): Observable<MaterialRequest> {
    return this.http.post<MaterialRequest>(`${this.apiUrl}/my-requests/${id}/status`, {
      status,
      remarks
    });
  }

  /**
   * Get vendor options for a specific material request
   */
  getVendorOptions(mrId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/my-requests/${mrId}/vendors`);
  }

  /**
   * Assign vendors to material request
   */
  assignVendors(mrId: number, vendorIds: number[]): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/my-requests/${mrId}/assign-vendors`, {
      vendorIds
    });
  }

  // Private helper methods

  /**
   * Build HTTP query parameters from filters
   */
  private buildQueryParams(filters: MaterialRequestFilters): HttpParams {
    let params = new HttpParams();

    // Pagination
    if (filters.page !== undefined) {
      params = params.set('page', filters.page.toString());
    }
    if (filters.pageSize !== undefined) {
      params = params.set('size', filters.pageSize.toString());
    }

    // Sorting - combine sortBy and sortOrder into single parameter: sort=fieldName,direction
    if (filters.sortBy) {

      let sortField = filters.sortBy;

      if(filters.sortBy ==='mrNumber') {
        sortField = 'prchOrdrId';
      }
      else if(filters.sortBy ==='productName') {
        sortField = 'mvtItemName';
      }
      else if(filters.sortBy ==='category') {
        sortField = 'cappCategoriesName';
      }
      else if(filters.sortBy ==='quantity') {
        sortField = 'quantity';
      }
      else if(filters.sortBy ==='unit') {
        sortField = 'optionName';
      }
      else if(filters.sortBy ==='status') {
        sortField = 'statusCd';
      }
      else if(filters.sortBy ==='deliveryDate') {
        sortField = 'deliveryDate';
      }
      else if(filters.sortBy ==='createdDate') {
        sortField = 'createdAt';
      }
      else if(filters.sortBy ==='vendorName') {
        sortField = 'vendorName';
      }


      const sortValue = filters.sortOrder 
        ? `${sortField},${filters.sortOrder}`
        : sortField;

      params = params.set('sort', sortValue);

    }

    // Search
    if (filters.search) {
      params = params.set('search', filters.search);
    }

    // Multi-select filters
    if (filters.projectAddressId?.length) {
      params = params.set('projectAddressId', filters.projectAddressId.join(','));
    }
    if (filters.mvtItemId?.length) {
      params = params.set('mvtItemId', filters.mvtItemId.join(','));
    }
    if (filters.vendorId?.length) {
      params = params.set('vendorId', filters.vendorId.join(','));
    }
    if (filters.statusCdId?.length) {
      params = params.set('statusCdId', filters.statusCdId.join(','));
    }
    if (filters.cappCategoriesId?.length) {
      params = params.set('cappCategoriesId', filters.cappCategoriesId.join(','));
    }

    // Date filters
    if (filters.deliveryDateFrom) {
      params = params.set('deliveryDateFrom', filters.deliveryDateFrom.toISOString());
    }
    if (filters.deliveryDateTo) {
      params = params.set('deliveryDateTo', filters.deliveryDateTo.toISOString());
    }
    if (filters.createdDateFrom) {
      params = params.set('createdDateFrom', filters.createdDateFrom.toISOString());
    }
    if (filters.createdDateTo) {
      params = params.set('createdDateTo', filters.createdDateTo.toISOString());
    }

    // Boolean filters
    if (filters.isMr !== undefined) {
      params = params.set('isMr', filters.isMr.toString());
    }
    if (filters.mrReadYn !== undefined) {
      params = params.set('mrReadYn', filters.mrReadYn);
    }
    if (filters.hasPrice !== undefined) {
      params = params.set('hasPrice', filters.hasPrice.toString());
    }
    if (filters.hasInstructions !== undefined) {
      params = params.set('hasInstructions', filters.hasInstructions.toString());
    }
    if (filters.hasImages !== undefined) {
      params = params.set('hasImages', filters.hasImages.toString());
    }

    // Range filters
    if (filters.quantityMin !== undefined) {
      params = params.set('quantityMin', filters.quantityMin.toString());
    }
    if (filters.quantityMax !== undefined) {
      params = params.set('quantityMax', filters.quantityMax.toString());
    }
    if (filters.priceMin !== undefined) {
      params = params.set('priceMin', filters.priceMin.toString());
    }
    if (filters.priceMax !== undefined) {
      params = params.set('priceMax', filters.priceMax.toString());
    }

    // Location filters
    if (filters.city?.length) {
      params = params.set('city', filters.city.join(','));
    }
    if (filters.state?.length) {
      params = params.set('state', filters.state.join(','));
    }
    if (filters.pincode?.length) {
      params = params.set('pincode', filters.pincode.join(','));
    }

    // Other filters
    if (filters.customerName) {
      params = params.set('customerName', filters.customerName);
    }
    if (filters.vendorRating !== undefined) {
      params = params.set('vendorRating', filters.vendorRating.toString());
    }
    if (filters.assignedVendorsCount !== undefined) {
      params = params.set('assignedVendorsCount', filters.assignedVendorsCount.toString());
    }

    return params;
  }

  /**
   * Transform MaterialRequest to MaterialRequestTableData
   */
  private transformToTableData(mr: MaterialRequest): MaterialRequestTableData {
    // Get status info with fallback to 'NEW' if statusCd not found, then to 'Unknown' if still not found
    let statusInfo = STATUS_CONFIG[mr.statusCd];
    
    if (!statusInfo) {
      // Try fallback to 'NEW' if statusCd is 'NEWW' or similar
      statusInfo = STATUS_CONFIG['NEW'];
    }
    
    // If still not found, use default 'Unknown' status
    if (!statusInfo || !statusInfo.name) {
      statusInfo = {
        code: mr.statusCd || 'UNKNOWN',
        name: 'Unknown',
        color: 'gray',
        icon: 'heroicons_outline:question-mark-circle'
      };
    }

    // The status column shows the API's quotationStatus whenever the backend sends one,
    // and falls back to the request's own statusCd otherwise. quotationStatus may arrive
    // as a code or as a ready-made label; resolveStatusInfo handles both, and an
    // unrecognised label is shown verbatim while keeping the statusCd colour/icon.
    const quotationStatus = mr.quotationStatus?.trim() || null;
    const quotationStatusInfo = resolveStatusInfo(quotationStatus);
    const displayStatusName = quotationStatus
      ? (quotationStatusInfo?.name || quotationStatus)
      : (statusInfo.name || 'Unknown');
    const displayStatusColor = quotationStatusInfo?.color || statusInfo.color || 'gray';
    const displayStatusIcon =
      quotationStatusInfo?.icon || statusInfo.icon || 'heroicons_outline:question-mark-circle';

    return {
      id: mr.prchOrdrId,
      prchOrdrId: mr.prchOrdrId,
      prchOrdrSplitId: mr.prchOrdrSplitId,
      orderGroupId: mr.orderGroupId ?? null,
      sellerVendorId: mr.sellerVendorId ?? null,
      statusCd: mr.statusCd,
      deliveryAddressId: mr.deliveryAddress?.id ?? mr.deliveryAddressId ?? null,
      lorryDriverName: mr.deliveryAddress?.lorryDriverName ?? null,
      lorryNumber: mr.deliveryAddress?.lorryNumber ?? null,
      lorryDriverPhone: mr.deliveryAddress?.lorryDriverPhone ?? null,
      mrNumber: mr.orderGroupName,
      projectName: mr.projectName,
      productName: mr.mvtItemName,
      category: mr.cappCategoriesName,
      quantity: mr.quantity,
      unit: mr.optionName || 'Units',
      status: mr.statusCd,
      quotationStatus,
      statusName: displayStatusName,
      statusColor: displayStatusColor,
      statusIcon: displayStatusIcon,
      deliveryDate: new Date(mr.deliveryDate),
      createdDate: new Date(mr.createdAt),
      updatedDate: new Date(mr.updatedAt),
      submittedDate: mr.prchOrdrSubmittedAt ? new Date(mr.prchOrdrSubmittedAt) : null,
      vendorName: mr.vendorName,
      customerName: mr.customerName,
      location: this.formatDeliveryAddress(mr),
      isMr: mr.isMr,
      isNego: mr.isNego,
      mrReadYn: mr.mrReadYn,
      quoteSubmittedYn: mr.quoteSubmittedYN ?? null,
      assignedVendorsCount: mr.assignedCustomers.length,
      price: mr.price,
      images: [mr.image1, mr.image2, mr.image3, mr.image4, mr.image5].filter(Boolean) as string[],
      hasInstructions: !!mr.instructions?.trim(),
      priority: this.calculatePriority(mr),
      urgency: this.calculateUrgency(mr),
      noOfProducts: mr.noOfProducts,
      products: mr.products ?? [],
      accessRestrictions: this.formatAccessRestrictions(mr),
      priorityLevel: mr.priorityLevel ?? null,
      timeSlotLabel: mr.prchOrdrDeliveryTimeSlot?.label ?? null,
      timeSlotCode: mr.prchOrdrDeliveryTimeSlot?.code ?? null,
      receivingContactName: mr.receivingPerson?.name ?? mr.receivingContactPersonName ?? null,
      receivingContactPhone: mr.receivingPerson?.phone ?? mr.receivingContactPersonNumber ?? null,
      receivingContactDesignation: mr.receivingPerson?.designation ?? null,
      negotiationContactName: mr.negotiateContactPerson?.name ?? null,
      negotiationContactPhone: mr.negotiateContactPerson?.phone ?? null,
      negotiationContactDesignation: mr.negotiateContactPerson?.designation ?? null,
      negotiationContactEmail: mr.negotiateContactPerson?.email ?? null
    };
  }

  /**
   * Combine access restriction signals from the API response.
   * Falls back to siteAccess/roadAccess when accessRestrictions is empty.
   */
  private formatAccessRestrictions(mr: MaterialRequest): string | null {
    if (mr.accessRestrictions?.trim()) {
      return mr.accessRestrictions.trim();
    }
    const parts = [mr.siteAccess?.trim(), mr.roadAccess?.trim()]
      .filter((p): p is string => !!p);
    if (parts.length === 0) return null;
    // De-duplicate when site and road access carry the same label.
    return Array.from(new Set(parts)).join(' · ');
  }

  /**
   * Format delivery address in a readable way, handling null values
   */
  private formatDeliveryAddress(mr: MaterialRequest): string {
    const addressParts: string[] = [];
    
    // Add address lines if they exist
    if (mr.deliveryAddressLine1?.trim()) {
      addressParts.push(mr.deliveryAddressLine1.trim());
    }
    if (mr.deliveryAddressLine2?.trim()) {
      addressParts.push(mr.deliveryAddressLine2.trim());
    }
    
    // Add city, state, pincode
    const cityStateParts: string[] = [];
    if (mr.deliveryCity?.trim()) {
      cityStateParts.push(mr.deliveryCity.trim());
    }
    if (mr.deliveryState?.trim()) {
      cityStateParts.push(mr.deliveryState.trim());
    }
    if (cityStateParts.length > 0) {
      addressParts.push(cityStateParts.join(', '));
    }
    if (mr.deliveryPincode?.trim()) {
      addressParts.push(mr.deliveryPincode.trim());
    }
    
    // Add country if it exists and is different from default
    if (mr.deliveryCountry?.trim() && mr.deliveryCountry.trim().toLowerCase() !== 'india') {
      addressParts.push(mr.deliveryCountry.trim());
    }
    
    return addressParts.length > 0 ? addressParts.join(', ') : '';
  }

  /**
   * Calculate priority based on business rules
   */
  private calculatePriority(mr: MaterialRequest): 'high' | 'medium' | 'low' {
    const deliveryDate = new Date(mr.deliveryDate);
    const now = new Date();
    const daysUntilDelivery = Math.ceil((deliveryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDelivery <= 3) return 'high';
    if (daysUntilDelivery <= 7) return 'medium';
    return 'low';
  }

  /**
   * Calculate urgency based on business rules
   */
  private calculateUrgency(mr: MaterialRequest): 'urgent' | 'normal' {
    const deliveryDate = new Date(mr.deliveryDate);
    const now = new Date();
    const daysUntilDelivery = Math.ceil((deliveryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysUntilDelivery <= 1 ? 'urgent' : 'normal';
  }

  /**
   * Extract error message from HTTP error
   */
  private getErrorMessage(error: any): string {
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred. Please try again.';
  }
}